<script setup lang="ts">
import { computed } from 'vue'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import featureFlagsHttpService, {
  type FeatureFlag,
} from '@/services/http/featureFlagsHttpService.ts'

// Переключение фичи — деплой-решение (включить/выключить поведение на проде), поэтому только Admin.
definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const { t, locale } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const queryKey = ['admin', 'feature-flags']

const flagsQuery = useQuery({
  queryKey: computed(() => [...queryKey, locale.value]),
  queryFn: () => featureFlagsHttpService.getFeatureFlags(),
})

const flags = computed<FeatureFlag[]>(() => flagsQuery.data.value ?? [])

function notify(success: boolean, detail: string) {
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? t('common.success') : t('common.error'),
    detail,
    life: 3000,
  })
}

function flagLabel(flag: FeatureFlag): string {
  // Если для кода есть перевод — показываем его, иначе сам код (новый флаг без перевода не ломает UI).
  const key = `featureFlags.codes.${flag.code}`
  const translated = t(key)
  return translated === key ? flag.code : translated
}

function formatUpdatedAt(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const toggleMutation = useMutation({
  mutationFn: (vars: { code: string; isEnabled: boolean }) =>
    featureFlagsHttpService.setFeatureFlag(vars.code, vars.isEnabled),
  // Оптимистично двигаем переключатель сразу, откатываем на ошибке.
  onMutate: async (vars) => {
    await queryClient.cancelQueries({ queryKey })
    const previous = queryClient.getQueryData<FeatureFlag[]>([...queryKey, locale.value])
    queryClient.setQueryData<FeatureFlag[]>([...queryKey, locale.value], (old) =>
      (old ?? []).map((f) => (f.code === vars.code ? { ...f, isEnabled: vars.isEnabled } : f)),
    )
    return { previous }
  },
  onError: (_err, _vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData([...queryKey, locale.value], context.previous)
    }
    notify(false, t('featureFlags.toasts.failed'))
  },
  onSuccess: (_data, vars) => {
    notify(
      true,
      vars.isEnabled ? t('featureFlags.toasts.enabled') : t('featureFlags.toasts.disabled'),
    )
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey })
  },
})

function onToggle(flag: FeatureFlag, value: boolean) {
  toggleMutation.mutate({ code: flag.code, isEnabled: value })
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex items-center justify-between w-full">
      <h1 class="text-xl font-bold">{{ t('featureFlags.title') }}</h1>
    </div>

    <p class="text-sm text-surface-500 m-0">{{ t('featureFlags.subtitle') }}</p>

    <DataTable
      :value="flags"
      :loading="flagsQuery.isLoading.value"
      data-key="code"
      class="w-full"
      :pt="{ bodyRow: { 'data-test': 'feature-flag-row' } }"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('featureFlags.empty') }}</div>
      </template>

      <Column :header="t('featureFlags.columns.feature')">
        <template #body="{ data }">
          <div class="flex flex-col gap-1">
            <span class="font-medium" data-test="feature-flag-name">{{ flagLabel(data) }}</span>
            <code class="text-xs text-surface-500">{{ data.code }}</code>
          </div>
        </template>
      </Column>
      <Column :header="t('featureFlags.columns.description')">
        <template #body="{ data }">
          <span class="text-sm">{{ data.description ?? '—' }}</span>
        </template>
      </Column>
      <Column :header="t('featureFlags.columns.state')">
        <template #body="{ data }">
          <ToggleSwitch
            :model-value="data.isEnabled"
            :disabled="toggleMutation.isPending.value"
            data-test="feature-flag-switch"
            @update:model-value="onToggle(data, $event)"
          />
        </template>
      </Column>
      <Column :header="t('featureFlags.columns.updated')">
        <template #body="{ data }">
          <div class="flex flex-col">
            <span class="text-sm">{{ formatUpdatedAt(data.updatedAt) }}</span>
            <span v-if="data.updatedBy" class="text-xs text-surface-500">{{ data.updatedBy }}</span>
          </div>
        </template>
      </Column>
    </DataTable>
  </section>
</template>
